import React, { useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  useTheme,
  AccordionDetails,
  Typography,
  Chip,
  Stack,
  Divider,
  Avatar,
  Paper,
  Button,
  Fade,
  Alert,
  alpha,
} from "@mui/material";
import {
  Edit,
  Delete,
  ExpandMore,
  Add,
  Warning,
  ArrowForward,
  Person,
  AccessTime,
  Assignment,
  Shield,
  TrendingUp,
  TrendingDown,
  Remove,
  CheckCircle,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { QK } from "../../../../../../base/qk";
import { useSnackbar } from "notistack";
import RiskDialog from "./Dialog/RiskFormDialog";
import {
  deleteRisk,
  getRisks,
} from "../../../../../../api/services/riskProfile";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import { useParams } from "react-router-dom";

// Constants with better labels
const IMPACT_LABELS = {
  1: "Minimal Impact",
  2: "Minor Impact",
  3: "Moderate Impact",
  4: "Major Impact",
  5: "Critical Impact",
};

const LIKELIHOOD_LABELS = {
  1: "Very Unlikely",
  2: "Unlikely",
  3: "Possible",
  4: "Likely",
  5: "Very Likely",
};

const getRiskStatus = (impact, likelihood) => {
  const score = (Number(impact) || 0) * (Number(likelihood) || 0);
  if (score >= 20) return { 
    label: "CRITICAL", 
    color: "#b91c1c", 
    bg: "#fef2f2", 
    border: "#fecaca",
    severity: "error",
    icon: "🔴"
  };
  if (score >= 15) return { 
    label: "HIGH", 
    color: "#d97706", 
    bg: "#fffbeb", 
    border: "#fde68a",
    severity: "warning",
    icon: "🟠"
  };
  if (score >= 8) return { 
    label: "MEDIUM", 
    color: "#2563eb", 
    bg: "#eff6ff", 
    border: "#93c5fd",
    severity: "info",
    icon: "🔵"
  };
  if (score >= 4) return { 
    label: "LOW", 
    color: "#059669", 
    bg: "#ecfdf5", 
    border: "#6ee7b7",
    severity: "success",
    icon: "🟢"
  };
  return { 
    label: "MINIMAL", 
    color: "#6b7280", 
    bg: "#f9fafb", 
    border: "#d1d5db",
    severity: "default",
    icon: "⚪"
  };
};

const getTrendIcon = (initialScore, latestScore) => {
  if (latestScore < initialScore) {
    return <TrendingDown sx={{ color: "#059669", fontSize: 20 }} />;
  }
  if (latestScore > initialScore) {
    return <TrendingUp sx={{ color: "#dc2626", fontSize: 20 }} />;
  }
  return <Remove sx={{ color: "#9ca3af", fontSize: 20 }} />;
};

// Improved ScoreCard with better visibility
const ScoreCard = ({ label, impact, likelihood, status }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      p: 2.5,
      borderRadius: "14px",
      bgcolor: status.bg,
      border: `2px solid ${status.border}`,
      position: "relative",
      minWidth: { xs: "100%", sm: "auto" },
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: `0 8px 24px ${alpha(status.color, 0.15)}`,
      },
    }}
  >
    <Typography
      variant="overline"
      fontWeight={900}
      color={status.color}
      sx={{ 
        letterSpacing: 1.5, 
        textTransform: "uppercase",
        fontSize: "0.7rem",
      }}
    >
      {label} {status.icon}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={1.5} mt={0.5}>
      <Typography 
        variant="h3" 
        fontWeight={900} 
        color={status.color}
        sx={{ fontSize: "2.2rem", lineHeight: 1 }}
      >
        {impact * likelihood}
      </Typography>
      <Box>
        <Chip
          label={status.label}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.7rem",
            fontWeight: 900,
            bgcolor: status.color,
            color: "#ffffff",
            px: 1,
            "& .MuiChip-label": { px: 1 }
          }}
        />
        <Typography
          variant="caption"
          sx={{ 
            fontSize: "0.7rem", 
            color: "#6b7280", 
            display: "block",
            mt: 0.3,
            fontWeight: 500
          }}
        >
          Impact: {IMPACT_LABELS[impact]} • Likelihood: {LIKELIHOOD_LABELS[likelihood]}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

// Main Component
const RiskProfile = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const { clientID, carePlanID } = useParams();

  const { data: risks, isLoading: risksLoading, error } = useQuery(
    [QK.risks, clientID, carePlanID],
    () => getRisks(clientID, carePlanID),
    {
      enabled: !!clientID && !!carePlanID,
      refetchOnWindowFocus: false,
      retry: 2,
    }
  );

  const deleteMutation = useMutation(deleteRisk, {
    onSuccess: () => {
      enqueueSnackbar("Risk assessment deleted successfully", { variant: "success" });
      queryClient.invalidateQueries([QK.risks, clientID, carePlanID]);
      setShowConfirm(false);
      setDeleteId(null);
    },
    onError: (error) => {
      enqueueSnackbar(error.message || "Failed to delete risk assessment", { variant: "error" });
    },
  });

  const handleEdit = (risk) => {
    setEditingRisk(risk);
    setOpenDialog(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingRisk(null);
  };

  if (risksLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
        <CircularProgress size={40} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2, borderRadius: "12px" }}>
        Failed to load risk assessments. Please try again later.
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header - Improved Design */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: "16px",
          bgcolor: "#ffffff",
          border: "1px solid #e5e7eb",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                bgcolor: "#1e293b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Shield sx={{ color: "#ffffff", fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={900} color="#111827">
                Clinical Risk Management
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="#6b7280">
                  {risks?.results?.length || 0} risk assessments documented
                </Typography>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "#10b981",
                  }}
                />
                <Typography variant="caption" color="#10b981" fontWeight={600}>
                  Active
                </Typography>
              </Stack>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
            sx={{
              borderRadius: "12px",
              bgcolor: "#1e293b",
              px: 4,
              py: 1.2,
              fontWeight: 700,
              textTransform: "none",
              fontSize: "0.95rem",
              "&:hover": { 
                bgcolor: "#0f172a",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(30, 41, 59, 0.25)",
              },
              transition: "all 0.3s ease",
            }}
          >
            New Risk Assessment
          </Button>
        </Stack>
      </Paper>

      {/* Risk List */}
      {risks?.results?.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: "16px",
            bgcolor: "#fafbfc",
            border: "2px dashed #d1d5db",
          }}
        >
          <Box sx={{ fontSize: 64, mb: 2 }}>🛡️</Box>
          <Typography variant="h5" color="#111827" fontWeight={700} gutterBottom>
            No Risk Assessments
          </Typography>
          <Typography variant="body1" color="#6b7280" sx={{ mb: 3 }}>
            Start by creating your first clinical risk assessment to track and manage patient risks effectively.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
            sx={{
              borderRadius: "12px",
              bgcolor: "#1e293b",
              px: 4,
              py: 1.2,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { bgcolor: "#0f172a" },
            }}
          >
            Create Assessment
          </Button>
        </Paper>
      ) : (
        <Fade in timeout={500}>
          <Stack spacing={2.5}>
            {risks?.results?.map((risk) => {
              const initialStatus = getRiskStatus(
                risk.initial_impact,
                risk.initial_likelihood
              );
              const latestStatus = getRiskStatus(
                risk.latest_impact,
                risk.latest_likelihood
              );
              const targetStatus = getRiskStatus(
                risk.target_impact,
                risk.target_likelihood
              );

              const initialScore = (risk.initial_impact || 0) * (risk.initial_likelihood || 0);
              const latestScore = (risk.latest_impact || 0) * (risk.latest_likelihood || 0);
              const hasImproved = latestScore < initialScore;
              const hasWorsened = latestScore > initialScore;

              return (
                <Accordion
                  key={risk.id}
                  elevation={0}
                  sx={{
                    border: `2px solid ${latestStatus.border}`,
                    borderRadius: "16px !important",
                    mb: 1,
                    bgcolor: "#ffffff",
                    "&:before": { display: "none" },
                    "&:hover": {
                      borderColor: latestStatus.color,
                      boxShadow: `0 4px 20px ${alpha(latestStatus.color, 0.1)}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: "#6b7280" }} />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        margin: 0,
                      },
                      px: 3,
                      py: 1,
                    }}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              bgcolor: latestStatus.color,
                              flexShrink: 0,
                              boxShadow: `0 0 0 4px ${alpha(latestStatus.color, 0.15)}`,
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography 
                              variant="subtitle1" 
                              fontWeight={800} 
                              color="#111827"
                              sx={{ 
                                fontSize: "1.05rem",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {risk.risk_name || "Unnamed Risk"}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <AccessTime sx={{ fontSize: 14, color: "#9ca3af" }} />
                              <Typography variant="caption" color="#6b7280" fontWeight={500}>
                                Updated: {new Date(risk.updated_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </Typography>
                              {hasImproved && (
                                <Chip
                                  icon={<CheckCircle sx={{ fontSize: 12 }} />}
                                  label="Improved"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.6rem",
                                    fontWeight: 700,
                                    bgcolor: "#d1fae5",
                                    color: "#065f46",
                                    "& .MuiChip-label": { px: 0.5 }
                                  }}
                                />
                              )}
                              {hasWorsened && (
                                <Chip
                                  label="⚠ Needs Review"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.6rem",
                                    fontWeight: 700,
                                    bgcolor: "#fee2e2",
                                    color: "#991b1b",
                                    "& .MuiChip-label": { px: 0.5 }
                                  }}
                                />
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent={{ md: "flex-end" }}
                          spacing={1.5}
                          sx={{ flexWrap: "wrap", gap: 1 }}
                        >
                          <Chip
                            label={`Initial: ${initialStatus.label}`}
                            size="small"
                            sx={{
                              height: 28,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              bgcolor: initialStatus.bg,
                              color: initialStatus.color,
                              border: `1px solid ${initialStatus.border}`,
                            }}
                          />
                          {getTrendIcon(initialScore, latestScore)}
                          <Chip
                            label={`Current: ${latestStatus.label}`}
                            size="small"
                            sx={{
                              height: 28,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              bgcolor: latestStatus.bg,
                              color: latestStatus.color,
                              border: `2px solid ${latestStatus.border}`,
                            }}
                          />
                          <ArrowForward sx={{ fontSize: 16, color: "#d1d5db" }} />
                          <Chip
                            label={`Target: ${targetStatus.label}`}
                            size="small"
                            sx={{
                              height: 28,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              bgcolor: "#f9fafb",
                              color: "#6b7280",
                              border: "2px dashed #d1d5db",
                            }}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 3, pb: 3, bgcolor: "#fafbfc" }}>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} lg={8}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2.5}
                          mb={4}
                        >
                          <ScoreCard
                            label="Initial Assessment"
                            impact={risk.initial_impact}
                            likelihood={risk.initial_likelihood}
                            status={initialStatus}
                          />
                          <ScoreCard
                            label="Current Status"
                            impact={risk.latest_impact}
                            likelihood={risk.latest_likelihood}
                            status={latestStatus}
                          />
                          <ScoreCard
                            label="Target Goal"
                            impact={risk.target_impact}
                            likelihood={risk.target_likelihood}
                            status={targetStatus}
                          />
                        </Stack>

                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="overline"
                              fontWeight={900}
                              color="#1e293b"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1.5,
                                fontSize: "0.75rem",
                                letterSpacing: 1,
                              }}
                            >
                              <Warning sx={{ fontSize: 18, color: "#f59e0b" }} /> 
                              Hazard Details
                            </Typography>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2.5,
                                bgcolor: "#ffffff",
                                borderRadius: "12px",
                                border: "1px solid #e5e7eb",
                                minHeight: 80,
                              }}
                            >
                              <Typography variant="body2" color="#374151" lineHeight={1.8}>
                                {risk.risk_details || "No hazard details provided."}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="overline"
                              fontWeight={900}
                              color="#1e293b"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1.5,
                                fontSize: "0.75rem",
                                letterSpacing: 1,
                              }}
                            >
                              <Assignment sx={{ fontSize: 18, color: "#2563eb" }} /> 
                              Management Plan
                            </Typography>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2.5,
                                bgcolor: "#ffffff",
                                borderRadius: "12px",
                                border: "1px solid #e5e7eb",
                                minHeight: 80,
                              }}
                            >
                              <Typography variant="body2" color="#374151" lineHeight={1.8}>
                                {risk.risk_management || "No management plan recorded."}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12} lg={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            bgcolor: risk.contingency_required ? "#fffbeb" : "#f9fafb",
                            borderRadius: "14px",
                            border: risk.contingency_required
                              ? "2px solid #f59e0b"
                              : "2px dashed #d1d5db",
                            height: "100%",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={900}
                            gutterBottom
                            sx={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: 1,
                              color: risk.contingency_required ? "#92400e" : "#6b7280",
                              fontSize: "0.9rem",
                            }}
                          >
                            <Shield
                              sx={{
                                color: risk.contingency_required ? "#f59e0b" : "#9ca3af",
                                fontSize: 22,
                              }}
                            />
                            Contingency Plan
                          </Typography>
                          {risk.contingency_required ? (
                            <Box>
                              <Typography
                                variant="caption"
                                color="#92400e"
                                fontWeight={800}
                                sx={{ 
                                  display: "block",
                                  mb: 0.5,
                                  letterSpacing: 0.5,
                                }}
                              >
                                ⚡ EMERGENCY ACTION REQUIRED
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="#78350f"
                                sx={{ mt: 1, lineHeight: 1.6 }}
                              >
                                {risk.action_required || "Action required but not specified."}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              color="#6b7280"
                              fontStyle="italic"
                              sx={{ mt: 1 }}
                            >
                              Routine care management suffices. No contingency action required at this time.
                            </Typography>
                          )}
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          sx={{ 
                            mt: 2, 
                            pt: 2.5, 
                            borderTop: "2px solid #e5e7eb" 
                          }}
                          spacing={1.5}
                        >
                          <Chip
                            avatar={
                              <Avatar
                                sx={{
                                  bgcolor: "#1e293b",
                                  width: 28,
                                  height: 28,
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  color: "#ffffff",
                                }}
                              >
                                {risk.created_by_name ? (
                                  risk.created_by_name.charAt(0).toUpperCase()
                                ) : (
                                  <Person sx={{ fontSize: 16 }} />
                                )}
                              </Avatar>
                            }
                            label={
                              <Typography variant="body2" fontWeight={600} color="#374151">
                                Assessed by: {risk.created_by || "Unknown"}
                              </Typography>
                            }
                            size="medium"
                            sx={{
                              bgcolor: "#f3f4f6",
                              border: "none",
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Button
                              startIcon={<Edit sx={{ fontSize: 18 }} />}
                              onClick={() => handleEdit(risk)}
                              variant="text"
                              size="small"
                              sx={{
                                color: "#2563eb",
                                fontWeight: 600,
                                textTransform: "none",
                                "&:hover": { 
                                  bgcolor: "#eff6ff",
                                  transform: "scale(1.05)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              startIcon={<Delete sx={{ fontSize: 18 }} />}
                              onClick={() => handleDeleteClick(risk.id)}
                              variant="text"
                              size="small"
                              sx={{
                                color: "#dc2626",
                                fontWeight: 600,
                                textTransform: "none",
                                "&:hover": { 
                                  bgcolor: "#fef2f2",
                                  transform: "scale(1.05)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Fade>
      )}

      <RiskDialog
        open={openDialog}
        handleClose={handleDialogClose}
        editingRisk={editingRisk}
      />

      <ConfirmModal
        show={showConfirm}
        handleClose={() => {
          setShowConfirm(false);
          setDeleteId(null);
        }}
        handleConfirm={handleConfirmDelete}
        title="Delete Risk Assessment"
        body="Are you sure you want to delete this clinical risk assessment? This action cannot be undone."
        isDelete
        isLoading={deleteMutation.isLoading}
      />
    </Box>
  );
};

export default RiskProfile;